"use client";

import { DateRangePicker } from "@/components/date-range-picker";
import { useHeader } from "@/components/hooks/data-api";
import Question from "@/components/question";
import { Search } from "@/components/search-input";
import { ShareHolder } from "@/components/shareholder";
import ThreadAnswer from "@/components/thread-answer";
import ThreadQuestion from "@/components/thread-question";
import { TickerFormField } from "@/components/ticker-form-field";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/components/ui/use-toast";
import { cn, extractDate } from "@/lib/utils";
import { formatHit } from "@/provider/ai/tools";
import {
  AiAskRequest,
  AiAskResponse,
  AiSearchRequest,
  AiSearchResponse,
} from "@/provider/ai/type";
import { OptionsContext } from "@/provider/options-provider";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter, useSearchParams } from "next/navigation";
import { useContext, useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { Control, FieldValues, useForm } from "react-hook-form";
import { IoEnter } from "react-icons/io5";
import { z } from "zod";
import { SettingsMenu } from "./settings-menu";
import SettingsPanel from "./settings-panel";
import { SummarizeClearButton } from "./summarize-clear-button";
import { CombinePdfButton } from "./combine-pdf-button";
import { PplxMessage, PplxResponse } from "@/provider/perplexity/type";
import { getCompany } from "@/config/companies";

export function SearchForm({ showSearch = true }: { showSearch?: boolean }) {
  const {
    searchText,
    searchOptions,
    setSearchResults,
    setLoadingResults,
    setGroupSummaryItems,
    setParams,
    setSearchText,
    setAggregations,
    setItemCount,
    searchResults,
    drawerOpen,
    setDrawerOpen,
  } = useContext(OptionsContext);

  const { toast } = useToast();
  const { data } = useHeader();
  const [threadLoading, setThreadLoading] = useState(false);
  const [extResultLoading, setExtResultLoading] = useState(false);
  const [initialQuestion, setInitialQuestion] = useState<string>("");
  const router = useRouter();
  const [result, setResult] = useState<AiAskResponse>();
  const [extResult, setExtResult] = useState<PplxResponse | null>();
  const [inputText, setInputText] = useState<string>("");

  const FormSchema = z
    .object({
      ticker: z.string(),
      searchText: z.string(),
      dateRange: z.object({
        from: z.date().optional(),
        to: z.date().optional(),
      }),
      validation: z.null().optional(),
    })
    .partial()
    .superRefine((data, ctx) => {
      if (!data.ticker && !data.searchText) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["validation"],
          message: "Enter a ticker or search text",
        });
      }
      if (!data.dateRange) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["dateRange"],
          message: "Select a date range",
        });
      }
      if (!data.dateRange?.from || !data.dateRange?.to) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["dateRange"],
          message: "Invalid date range",
        });
      }
    });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      ticker: "",
      searchText: "",
      dateRange: searchOptions.dateRange,
    },
    values: {
      ticker: searchOptions.ticker ?? "",
      searchText: searchText ?? "",
      dateRange: searchOptions.dateRange,
    },
  });

  const getSearchResults = async (criteria: AiSearchRequest) => {
    setLoadingResults(true);
    const response = await fetch("/api/search", {
      method: "POST",
      body: JSON.stringify(criteria),
      cache: "default",
    });
    setLoadingResults(false);
    const result: AiSearchResponse = await response.json();
    if (response.ok) {
      return result;
    } else {
      toast({
        title: "Error",
        description: "Failed to fetch search results",
        variant: "destructive",
      });
    }
  };

  async function onSubmit() {
    let searchResponse: AiSearchResponse | undefined;
    setGroupSummaryItems([]);
    setResult(undefined);
    setInitialQuestion("");
    setInputText("");

    const request = {
      period: form.getValues("dateRange") as DateRange,
      ticker: searchOptions.ticker,
      include_blogs: searchOptions.switches.blogs,
      include_forums: searchOptions.switches.forums,
      include_research: searchOptions.switches.research,
      include_transcripts: searchOptions.switches.transcripts,
      show_details: searchOptions.switches.details,
      price_charts: false,
      search_text: form.getValues("searchText") ?? undefined,
      max_items: searchOptions.maxSearchResults,
      fragment_size: searchOptions.fragmentSize,
      max_fragments: searchOptions.maxFragments,
      summarizer_name: searchOptions.summarizer_name,
      order_by: searchOptions.order_by,
    };

    searchResponse = await getSearchResults(request);
    if (Array.isArray(searchResponse?.hits)) {
      setSearchResults(searchResponse.hits);
      setAggregations(searchResponse.aggregations);
      setItemCount(searchResponse.item_count);

      const extract =
        searchResponse?.hits?.map((r) => formatHit(r)).join("") ?? "";
      setInputText(extract);
    } else {
      toast({
        title: "Error",
        description: "Failed to fetch search results",
        variant: "destructive",
      });
    }

    setParams(request);

    if (searchOptions.question && searchOptions.question !== "") {
      setDrawerOpen(true);
      setInitialQuestion(searchOptions.question);
      setThreadLoading(true);

      const ids: string[] =
        searchResponse?.hits?.map((x) => `${x.shortCode}${x.id}`) ?? [];

      if (ids.length > 0) {
        // Ask the question
        const questionRequest: AiAskRequest = {
          prompt: searchOptions.question,
          ids: ids,
          event_type: "Q_AND_A_SEARCH",
          system_prompt: searchOptions.systemPrompt,
          input_text: inputText,
          keywords: [searchText],
          ticker: searchOptions.ticker,
          period: `${extractDate(request.period?.from)} to ${extractDate(
            request.period?.to,
          )}`,
        };

        let result: AiAskResponse;

        try {
          setResult(undefined);
          setExtResult(null);
          const response = await fetch("/api/ask", {
            method: "POST",
            body: JSON.stringify(questionRequest),
            cache: "default",
          });

          result = await response.json();
          setResult(result);
          setThreadLoading(false);
          setExtResultLoading(true);
          await getExternalResults(
            questionRequest.prompt,
            searchOptions.ticker,
            [searchText],
            setExtResult,
            setExtResultLoading,
            result.id,
          );
          setExtResultLoading(false);
        } catch (error) {
          setThreadLoading(false);
          setExtResultLoading(false);
          setResult(undefined);
          setExtResult(null);
        }
      } else {
        setThreadLoading(false);
        setInitialQuestion("");
        setDrawerOpen(false);
        toast({
          title: "Error",
          description: "No search results to ask question",
          variant: "destructive",
        });
      }
      setThreadLoading(false);
    }

    if (!showSearch) {
      router.push("/home");
    }
  }

  // Check if search query is in URL
  const searchParams = useSearchParams();
  const search = searchParams.get("search");
  useEffect(() => {
    if (!search || search === "") return;

    setSearchText(search);
    form.setValue("searchText", search);
    form.handleSubmit(onSubmit)();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Select all after search
  useEffect(() => {
    const selectAll = async () => {
      const allItems = searchResults
        ? searchResults?.map((x) => {
            return { id: x.id, type: x.shortCode };
          })
        : [];
      setGroupSummaryItems(allItems.flat());
    };
    selectAll();
  }, [searchResults, setGroupSummaryItems]);

  return (
    <Drawer
      open={drawerOpen}
      onOpenChange={(open) => {
        setDrawerOpen(open);
      }}
    >
      <div
        className={cn(
          "duration-2000 top-0 z-50 transform bg-white pt-0 shadow-lg",
          "pl-0",
          "relative md:sticky",
        )}
      >
        {form && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <ShareHolder form={form} showSearch={showSearch}></ShareHolder>
              {showSearch && (
                <>
                  <div className="mb-2 mt-2 flex w-full flex-col items-center xl:mb-3 xl:mt-2 xl:flex-row xl:justify-between xl:pr-8">
                    {/* Greeting & Keywords */}
                    <div className="mb-2 mr-0 flex w-full shrink-0 flex-col items-center px-4 sm:gap-x-6 sm:px-6 xl:top-0 xl:mb-0 xl:mr-2 xl:w-1/2 xl:flex-row xl:pl-2 xl:pr-0">
                      {/* <Greeting session={session!} status={status}></Greeting> */}
                      <div className="mb-0 flex w-full">
                        <div className="hidden xl:block">
                          <SettingsMenu />
                        </div>

                        {/* Ticker & Period */}
                        <div className="mb-2 flex w-full flex-col justify-start space-y-2 px-0 xl:mb-0 xl:w-auto xl:flex-row xl:items-center xl:space-y-0">
                          <div className="px-0">
                            <TickerFormField form={form} />
                          </div>
                          <div className="px-0">
                            <FormField
                              name="dateRange"
                              render={() => (
                                <FormItem>
                                  <FormControl>
                                    <DateRangePicker
                                      className="rounded-sm shadow-sm xl:ml-2 xl:mr-2"
                                      form={form}
                                      onSubmit={onSubmit}
                                    />
                                  </FormControl>
                                </FormItem>
                              )}
                            ></FormField>
                          </div>
                        </div>
                      </div>
                    </div>
                    <Search
                      form={form}
                      className="h-full px-4 md:px-6 xl:px-0"
                      theme={data?.theme}
                    />
                  </div>
                  <div className="my-3 flex h-full w-full flex-col items-center pl-4 pr-4 md:flex-row lg:pl-6 lg:pr-6 2xl:pl-6 2xl:pr-8">
                    <div className="mr-0 flex h-10 w-full md:mr-2">
                      <Question form={form} />
                    </div>
                    {/* Buttons */}
                    <div className="ml-0 mt-2 flex flex-row md:ml-0 md:mt-0 xl:items-center">
                      <div className="hidden md:flex xl:hidden">
                        <SettingsMenu />
                      </div>
                      <SettingsPanel />
                      <Button
                        type="submit"
                        className="mr-2 bg-indigo-400 hover:bg-indigo-500"
                        variant={"outline"}
                        title="Search"
                        disabled={form.formState.isSubmitting}
                      >
                        <IoEnter className="h-8 w-8" />
                      </Button>
                      <SummarizeClearButton className="" />
                      <CombinePdfButton className="ml-2" />
                      {/* <SummarizeButton /> */}
                    </div>
                  </div>
                  <Validation control={form.control}></Validation>
                </>
              )}
            </form>
          </Form>
        )}
        <DrawerContent className="min-h-[calc(100vh-8rem)] bg-sh-shareholder2016-base">
          <div>
            <div className="flex h-fit w-full flex-row items-center justify-between px-2">
              <div className="my-0 flex h-fit w-full">
                <ThreadQuestion
                  drawerOpen={drawerOpen}
                  setDrawerOpen={setDrawerOpen}
                  threadLoading={threadLoading}
                  setThreadLoading={setThreadLoading}
                  result={result}
                  setResult={setResult}
                  setInitialQuestion={setInitialQuestion}
                  extResult={extResult}
                  setExtResult={setExtResult}
                  extResultLoading={extResultLoading}
                  setExtResultLoading={setExtResultLoading}
                  inputText={inputText}
                />
              </div>
            </div>
            <ThreadAnswer
              drawerOpen={drawerOpen}
              setDrawerOpen={setDrawerOpen}
              threadLoading={threadLoading}
              setThreadLoading={setThreadLoading}
              result={result}
              setResult={setResult}
              question={searchOptions.question!}
              keywords={searchText}
              ticker={searchOptions.ticker}
              initialQuestion={initialQuestion}
              extResult={extResult}
              setExtResult={setExtResult}
              setExtResultLoading={setExtResultLoading}
            />
          </div>
        </DrawerContent>
      </div>
    </Drawer>
  );
}

function Validation(props: { control: Control<FieldValues> | undefined }) {
  return (
    <div className="flex w-full flex-row items-center justify-center xl:justify-start">
      <FormField
        control={props.control}
        name="validation"
        render={() => (
          <FormItem>
            <FormMessage className="ml-0 xl:ml-[275px]" />
          </FormItem>
        )}
      />
    </div>
  );
}

export const getExternalResults = async (
  question: string,
  ticker: string,
  keywords: string[],
  setExtResult: (result: PplxResponse) => void,
  setExtResultLoading: (loading: boolean) => void,
  feedbackId?: string,
) => {
  const systemContent: string[] = [
    `You will act as an advisor to a financial portfolio manager who invests in global equity markets. Use numbers where applicable.`,
  ];

  const userContent: string[] = [];
  if (ticker)
    userContent.push(
      `Answer in relation to ticker ${getCompany(ticker)?.CompanyName ?? ticker}.`,
    );
  if (keywords) {
    if (
      keywords.filter((k) => {
        return k !== "";
      }).length > 0
    ) {
      userContent.push(`Keywords: ${keywords.join(", ")}.`);
    }
  }
  if (question) {
    userContent.push(question);
  }
  const q = userContent.join(" ");
  const messages: PplxMessage[] = [
    {
      role: "system",
      content: systemContent.join(" "),
    },
    {
      role: "user",
      content: `${q}${q.endsWith("?") ? "" : "?"}`,
    },
  ];

  setExtResultLoading(true);
  const result = await fetch("/api/pplx", {
    method: "POST",
    body: JSON.stringify({
      messages,
      feedbackId,
    }),
  });

  const response: PplxResponse = await result.json();

  setExtResult(response);
  setExtResultLoading(false);
};
